import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { RootState, AppDispatch } from "@/store/store";
import { ROLE_LABELS } from "@/types/auth";
import { setUser } from "@/store/slices/authSlice";
import { getApiFieldErrors } from "@/services/api";
import { getUserPreferencesErrorMessage, userPreferencesService } from "@/services/userPreferences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface EmailFormData {
  email: string;
}

interface NameFormData {
  first_name: string;
  last_name: string;
}

interface CompanyNameFormData {
  company_name: string;
}

const formatLastLogin = (value?: string | null) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const ProfileAccountForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdminRole =
    user?.role === "admin" || user?.role === "admin_support" || user?.role === "underwriter";
  const canEditCompanyName = user?.role === "broker";

  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailBannerError, setEmailBannerError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState<string | null>(null);
  const [nameBannerError, setNameBannerError] = useState<string | null>(null);
  const [companySuccess, setCompanySuccess] = useState<string | null>(null);
  const [companyBannerError, setCompanyBannerError] = useState<string | null>(null);

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    reset: resetEmail,
    setError: setEmailFieldError,
    clearErrors: clearEmailFieldErrors,
    formState: { errors: emailErrors, isDirty: emailDirty },
  } = useForm<EmailFormData>({
    defaultValues: { email: user?.email ?? "" },
  });

  const {
    register: registerName,
    handleSubmit: handleNameSubmit,
    reset: resetName,
    setError: setNameFieldError,
    clearErrors: clearNameFieldErrors,
    formState: { errors: nameErrors, isDirty: nameDirty },
  } = useForm<NameFormData>({
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
    },
  });

  const {
    register: registerCompany,
    handleSubmit: handleCompanySubmit,
    reset: resetCompany,
    setError: setCompanyFieldError,
    clearErrors: clearCompanyFieldErrors,
    formState: { errors: companyErrors, isDirty: companyDirty },
  } = useForm<CompanyNameFormData>({
    defaultValues: { company_name: user?.company_name || "" },
  });

  useEffect(() => {
    resetEmail({ email: user?.email ?? "" });
  }, [user?.email, resetEmail]);

  useEffect(() => {
    resetName({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
    });
  }, [user?.first_name, user?.last_name, resetName]);

  useEffect(() => {
    if (!canEditCompanyName) return;
    resetCompany({ company_name: user?.company_name || "" });
  }, [canEditCompanyName, user?.company_name, resetCompany]);

  const emailMutation = useMutation({
    mutationFn: (email: string) => userPreferencesService.update({ email }),
    onSuccess: (updatedUser) => {
      dispatch(setUser(updatedUser));
      setEmailBannerError(null);
      clearEmailFieldErrors();
      if (updatedUser.pending_email) {
        setEmailSuccess(
          `Confirmation sent to ${updatedUser.pending_email}. Your login email stays ${updatedUser.email} until you confirm.`
        );
        resetEmail({ email: updatedUser.email });
      } else {
        setEmailSuccess("Pending email change cleared.");
        resetEmail({ email: updatedUser.email });
      }
    },
    onError: (err) => {
      setEmailSuccess(null);
      const fields = getApiFieldErrors(err);
      if (fields.email) {
        setEmailFieldError("email", { type: "server", message: fields.email });
        setEmailBannerError(null);
      } else {
        setEmailBannerError(getUserPreferencesErrorMessage(err));
      }
    },
  });

  const nameMutation = useMutation({
    mutationFn: (payload: NameFormData) => userPreferencesService.update(payload),
    onSuccess: (updatedUser) => {
      dispatch(setUser(updatedUser));
      setNameBannerError(null);
      clearNameFieldErrors();
      setNameSuccess("Name updated successfully");
      resetName({
        first_name: updatedUser.first_name || "",
        last_name: updatedUser.last_name || "",
      });
    },
    onError: (err) => {
      setNameSuccess(null);
      const fields = getApiFieldErrors(err);
      let fieldError = false;
      if (fields.first_name) {
        setNameFieldError("first_name", { type: "server", message: fields.first_name });
        fieldError = true;
      }
      if (fields.last_name) {
        setNameFieldError("last_name", { type: "server", message: fields.last_name });
        fieldError = true;
      }
      if (fieldError) {
        setNameBannerError(null);
      } else {
        setNameBannerError(getUserPreferencesErrorMessage(err));
      }
    },
  });

  const companyMutation = useMutation({
    mutationFn: (company_name: string) => userPreferencesService.update({ company_name }),
    onSuccess: (updatedUser) => {
      dispatch(setUser(updatedUser));
      setCompanyBannerError(null);
      clearCompanyFieldErrors();
      setCompanySuccess("Company name updated successfully");
      resetCompany({ company_name: updatedUser.company_name || "" });
    },
    onError: (err) => {
      setCompanySuccess(null);
      const fields = getApiFieldErrors(err);
      if (fields.company_name) {
        setCompanyFieldError("company_name", {
          type: "server",
          message: fields.company_name,
        });
        setCompanyBannerError(null);
      } else {
        setCompanyBannerError(getUserPreferencesErrorMessage(err));
      }
    },
  });

  const onEmailSubmit = (data: EmailFormData) => {
    setEmailSuccess(null);
    setEmailBannerError(null);
    clearEmailFieldErrors();
    const nextEmail = data.email.trim().toLowerCase();
    emailMutation.mutate(nextEmail);
  };

  const onNameSubmit = (data: NameFormData) => {
    setNameSuccess(null);
    setNameBannerError(null);
    clearNameFieldErrors();
    const first_name = data.first_name.trim();
    const last_name = data.last_name.trim();
    if (!first_name) {
      setNameFieldError("first_name", { type: "validate", message: "First name is required." });
      return;
    }
    if (!last_name) {
      setNameFieldError("last_name", { type: "validate", message: "Last name is required." });
      return;
    }
    nameMutation.mutate({ first_name, last_name });
  };

  const onCompanySubmit = (data: CompanyNameFormData) => {
    setCompanySuccess(null);
    setCompanyBannerError(null);
    clearCompanyFieldErrors();
    const nextName = data.company_name.trim();
    if (!nextName) {
      setCompanyFieldError("company_name", {
        type: "validate",
        message: "Company name cannot be empty.",
      });
      return;
    }
    if (nextName === (user?.company_name || "").trim()) {
      setCompanyFieldError("company_name", {
        type: "validate",
        message: "Enter a different company name to update.",
      });
      return;
    }
    companyMutation.mutate(nextName);
  };

  const personName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  const displayName =
    personName || user?.organization_name || user?.company_name || user?.email || "User";
  const avatarLetter = (user?.first_name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarFallback
              className={cn(
                "text-2xl font-medium",
                isAdminRole
                  ? "bg-secondary text-secondary-foreground"
                  : "bg-primary text-primary-foreground"
              )}
            >
              {avatarLetter}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 pr-2">
            <h2 className="break-words text-xl font-semibold">{displayName}</h2>
            <p className="break-all text-sm text-muted-foreground">{user?.email}</p>
            <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role ?? "broker"]}</p>
          </div>
        </div>

        <Separator />

        {emailSuccess && (
          <Alert className="relative border-green-600/50 bg-green-50 pr-10 text-green-900 dark:bg-green-950 dark:text-green-100">
            <AlertDescription>{emailSuccess}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-6 w-6"
              onClick={() => setEmailSuccess(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        {emailBannerError && (
          <Alert variant="destructive" className="relative pr-10">
            <AlertDescription>{emailBannerError}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-6 w-6 text-destructive hover:text-destructive"
              onClick={() => setEmailBannerError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        {user?.pending_email && (
          <Alert>
            <AlertDescription>
              Pending confirmation for <span className="font-medium">{user.pending_email}</span>.
              Check that inbox for the confirmation link. Your current login email remains{" "}
              <span className="font-medium">{user.email}</span> until confirmed.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleEmailSubmit(onEmailSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              type="email"
              autoComplete="email"
              {...registerEmail("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email address",
                },
              })}
              aria-invalid={!!emailErrors.email}
            />
            {emailErrors.email && (
              <p className="text-sm text-destructive">{emailErrors.email.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              We send a confirmation link to the new address before changing your login email.
            </p>
          </div>
          <Button type="submit" disabled={emailMutation.isPending || !emailDirty}>
            {emailMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Send Confirmation"
            )}
          </Button>
        </form>

        <Separator />

        {nameSuccess && (
          <Alert className="relative border-green-600/50 bg-green-50 pr-10 text-green-900 dark:bg-green-950 dark:text-green-100">
            <AlertDescription>{nameSuccess}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-6 w-6"
              onClick={() => setNameSuccess(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        {nameBannerError && (
          <Alert variant="destructive" className="relative pr-10">
            <AlertDescription>{nameBannerError}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-6 w-6 text-destructive hover:text-destructive"
              onClick={() => setNameBannerError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        )}

        <form onSubmit={handleNameSubmit(onNameSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-first-name">First name</Label>
              <Input
                id="profile-first-name"
                autoComplete="given-name"
                {...registerName("first_name", {
                  required: "First name is required",
                  validate: (value) => value.trim().length > 0 || "First name is required",
                })}
                aria-invalid={!!nameErrors.first_name}
              />
              {nameErrors.first_name && (
                <p className="text-sm text-destructive">{nameErrors.first_name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-last-name">Last name</Label>
              <Input
                id="profile-last-name"
                autoComplete="family-name"
                {...registerName("last_name", {
                  required: "Last name is required",
                  validate: (value) => value.trim().length > 0 || "Last name is required",
                })}
                aria-invalid={!!nameErrors.last_name}
              />
              {nameErrors.last_name && (
                <p className="text-sm text-destructive">{nameErrors.last_name.message}</p>
              )}
            </div>
          </div>
          <Button type="submit" disabled={nameMutation.isPending || !nameDirty}>
            {nameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Name"}
          </Button>
        </form>

        {canEditCompanyName && (
          <>
            <Separator />

            {companySuccess && (
              <Alert className="relative border-green-600/50 bg-green-50 pr-10 text-green-900 dark:bg-green-950 dark:text-green-100">
                <AlertDescription>{companySuccess}</AlertDescription>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 h-6 w-6"
                  onClick={() => setCompanySuccess(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Alert>
            )}

            {companyBannerError && (
              <Alert variant="destructive" className="relative pr-10">
                <AlertDescription>{companyBannerError}</AlertDescription>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 h-6 w-6 text-destructive hover:text-destructive"
                  onClick={() => setCompanyBannerError(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Alert>
            )}

            <form onSubmit={handleCompanySubmit(onCompanySubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-company-name">Company Name</Label>
                <Input
                  id="profile-company-name"
                  type="text"
                  {...registerCompany("company_name", {
                    required: "Company name is required",
                    validate: (value) => value.trim().length > 0 || "Company name cannot be empty",
                  })}
                  aria-invalid={!!companyErrors.company_name}
                />
                {companyErrors.company_name && (
                  <p className="text-sm text-destructive">{companyErrors.company_name.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Shown on applications and in funder workflows as your brokerage name.
                </p>
              </div>
              <Button type="submit" disabled={companyMutation.isPending || !companyDirty}>
                {companyMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Update Company Name"
                )}
              </Button>
            </form>
          </>
        )}

        <Separator />

        <dl className="space-y-4 text-sm">
          <div>
            <dt className="text-muted-foreground">Role</dt>
            <dd>{ROLE_LABELS[user?.role ?? "broker"]}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Account Status</dt>
            <dd className={user?.is_active ? "text-green-600" : "text-destructive"}>
              {user?.is_active ? "Active" : "Inactive"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Last login</dt>
            <dd>{formatLastLogin(user?.last_login)}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

export default ProfileAccountForm;
